import { Component, type ErrorInfo, type ReactNode } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { useCmtsSelector } from "@store/hooks.js";
import { selectCmtsUi } from "@store/selectors.js";
import { logCmtsSafeError, normalizeCmtsApiError } from "@cmts/errors/index.js";
import { ApiErrorPanel } from "./ApiErrorPanel.js";

type CmtsRouteErrorBoundaryState = {
  readonly error: unknown | null;
};

type CmtsRouteErrorBoundaryProps = {
  readonly children: ReactNode;
  readonly routePath?: string | undefined;
  readonly correlationId?: string | undefined;
};

export class CmtsRouteErrorBoundary extends Component<CmtsRouteErrorBoundaryProps, CmtsRouteErrorBoundaryState> {
  override state: CmtsRouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): CmtsRouteErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    logCmtsSafeError(error, { operation: "route-render", route: this.props.routePath });
  }

  private readonly recover = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      const normalized = normalizeCmtsApiError(this.state.error, { operation: "route-render", route: this.props.routePath });
      return (
        <main className="cmts-route-error-boundary" role="main">
          <ApiErrorPanel error={{ ...normalized, ...(this.props.correlationId ? { correlationId: this.props.correlationId } : {}) }} title="This CMTS page could not render safely" onRetry={this.recover} onRefresh={this.recover} />
        </main>
      );
    }
    return this.props.children;
  }
}

function routeErrorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`.trim();
  }
  if (error instanceof Error) return error.message;
  return "The requested CMTS route could not be loaded.";
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const correlationId = useCmtsSelector((state) => selectCmtsUi(state).lastCorrelationId);
  const normalized = normalizeCmtsApiError(
    isRouteErrorResponse(error) ? { status: error.status, code: error.status === 404 ? "missing_entity" : undefined, message: routeErrorMessage(error) } : error,
    { operation: "route-loader" },
  );
  return (
    <main className="cmts-route-error-boundary" role="main">
      <ApiErrorPanel error={{ ...normalized, ...(correlationId ? { correlationId } : {}) }} title="CMTS route loading failed" onBack={() => window.history.back()} onRefresh={() => window.location.reload()} />
    </main>
  );
}

function sanitizeComponentStack(stack: string): string {
  return stack.split("\n").slice(0, 8).join("\n");
}
